import { FC, Fragment, useEffect, useRef } from 'react';
import type { ProseBlock, RemediationReviewItem } from '@libs/types';
import { Box, type Theme, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

/** Escapes a token so it can go into a `RegExp` — `[` and `]` are syntax there. */
function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The whole document, with every blank shown in place.
 *
 * It used to be one sentence per field, cropped around the token. That answered
 * "what does this word sit next to" and nothing else — a user filling in a
 * reviewer's name could not see that the same document names a reviewer twice,
 * or what the clause above commits them to. A document is read as a document.
 *
 * Values appear as they are typed, so the page is a live proof of the finished
 * text rather than a form with a description of one. What is rendered here is
 * exactly what `applyPlaceholderValues` will substitute at finalize: same
 * tokens, same values, same rule that an empty answer leaves the token showing.
 */
export const PlaceholderDocumentPreview: FC<{
  item: RemediationReviewItem | undefined;
  /** Every token this document carries, so the renderer knows what to split on. */
  tokens: string[];
  /** The value a token currently resolves to — typed, or already in the profile. */
  resolve: (token: string) => string;
  /** The field the user is editing, brought into view and emphasised. */
  focusedToken: string | null;
}> = observer(({ item, tokens, resolve, focusedToken }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const focusedRef = useRef<HTMLSpanElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Bring the blank being edited into view. Only when it is off screen — a
  // document that jumps on every keystroke is unreadable, and `nearest` leaves
  // an already-visible token exactly where the eye left it.
  useEffect(() => {
    if (!focusedToken || !focusedRef.current) return;
    focusedRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusedToken]);

  const content = item?.generatedContent;

  if (!content || content.format !== 'prose') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
          {t('app.remediation.placeholders.noPreview')}
        </Typography>
      </Box>
    );
  }

  // One pattern for every token in the document, so a block is split in a single
  // pass and the parts keep their original order.
  const pattern = tokens.length
    ? new RegExp(`(${tokens.map(escapeForRegExp).join('|')})`, 'g')
    : null;

  const renderText = (text: string) => {
    if (!pattern) return text;
    return text.split(pattern).map((part, index) => {
      if (!tokens.includes(part)) return <Fragment key={index}>{part}</Fragment>;
      const value = resolve(part);
      const isFocused = part === focusedToken;
      return (
        <Box
          key={index}
          component="span"
          ref={isFocused ? focusedRef : undefined}
          sx={{
            fontWeight: 600,
            borderRadius: 0.75,
            px: 0.5,
            scrollMarginBlock: '96px',
            // Green once it reads as a real value, ocean-dashed while it is
            // still a token — the encoding the review canvas already uses.
            ...(value
              ? { backgroundColor: theme.palette.green[200], color: theme.palette.green[900] }
              : {
                  backgroundColor: theme.palette.ocean[100],
                  color: theme.palette.ocean[800],
                  border: `1px dashed ${theme.palette.ocean[400]}`,
                }),
            // The one being edited, findable in a page of prose.
            ...(isFocused
              ? { outline: `2px solid ${theme.palette.ocean[700]}`, outlineOffset: 2 }
              : {}),
          }}
        >
          {value || part}
        </Box>
      );
    });
  };

  const renderBlock = (block: ProseBlock, key: string) => {
    if (block.kind === 'table' && block.table) {
      return (
        <Box key={key} component="table" sx={tableSx(theme)}>
          <tbody>
            {[...(block.table.headerRows ?? []), ...(block.table.rows ?? [])].map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c}>{renderText(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      );
    }

    if (!block.text) return null;

    if (block.kind === 'heading') {
      return (
        <Typography
          key={key}
          sx={{
            // The AI's own level, clamped: a level-6 heading rendered at its
            // nominal size is smaller than the body it introduces.
            fontSize: block.level && block.level > 2 ? 14 : 15.5,
            fontWeight: 700,
            mt: 2,
            mb: 0.75,
          }}
        >
          {renderText(block.text)}
        </Typography>
      );
    }

    return (
      <Typography
        key={key}
        variant="textSM_regular"
        sx={{
          display: 'block',
          lineHeight: 1.8,
          mb: 1,
          ...(block.kind === 'list_item' ? { pl: 2, position: 'relative' } : {}),
        }}
      >
        {block.kind === 'list_item' && (
          <Box component="span" sx={{ position: 'absolute', insetInlineStart: 4 }}>
            •
          </Box>
        )}
        {renderText(block.text)}
      </Typography>
    );
  };

  return (
    <Box ref={scrollRef} sx={{ px: 3, py: 2.5 }}>
      {content.sections.map((section, s) => (
        <Box key={s} sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.tertiary',
              display: 'block',
              mb: 1,
            }}
          >
            {section.sectionName}
          </Typography>
          {section.blocks.map((block, b) => renderBlock(block, `${s}-${b}`))}
        </Box>
      ))}
    </Box>
  );
});

/**
 * Declared as a `function` rather than an arrow: an arrow at module scope
 * taking one argument and returning an object is indistinguishable from a
 * component to the lint rule that wraps components in `observer`, and it
 * wrapped this one.
 */
function tableSx(theme: Theme) {
  return {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12.5,
    mb: 1.5,
    '& td': {
      border: `1px solid ${theme.palette.grey[300]}`,
      padding: '6px 8px',
      verticalAlign: 'top',
    },
  } as const;
}
